import { useEffect, useRef, useState } from "react"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { useTranslation } from "react-i18next"
import RuleEngineBackend from "../services/RuleEngineBackend"
import PPUtils from "../../utils/PPUtils"
import { toast } from "react-toastify"
import { Dialog } from "primereact/dialog"
import { Calendar } from "primereact/calendar"
import { addLocale, FilterMatchMode } from "primereact/api"
import { usePricemanTablePageSize } from "../common/usePricemanTablePageSize"
import "../../styles/priceman.css"
import PPMatchingStatusBanner from "../../components/matching/PPMatchingStatusBanner"

addLocale("de", {
    firstDayOfWeek: 1,
    showMonthAfterYear: false,
    dayNames: ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
    dayNamesShort: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
    dayNamesMin: ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"],
    monthNames: [
        "Januar",
        "Februar",
        "März",
        "April",
        "Mai",
        "Juni",
        "Juli",
        "August",
        "September",
        "Oktober",
        "November",
        "Dezember"
    ],
    monthNamesShort: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
    today: "Heute",
    clear: "Löschen"
})

addLocale("fr", {
    firstDayOfWeek: 1,
    showMonthAfterYear: false,
    dayNames: ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"],
    dayNamesShort: ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."],
    dayNamesMin: ["D", "L", "M", "M", "J", "V", "S"],
    monthNames: [
        "janvier",
        "février",
        "mars",
        "avril",
        "mai",
        "juin",
        "juillet",
        "août",
        "septembre",
        "octobre",
        "novembre",
        "décembre"
    ],
    monthNamesShort: ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."],
    today: "Aujourd'hui",
    clear: "Effacer"
})

const SELECTED_ARTICLES_CONFIRM_THRESHOLD = 20

export default function PPPriceManagementExcludedArticles() {
    let auth = PPUtils.getLogin()
    const token = auth.token
    const customerId = PPUtils.getEffectiveCustomerId(auth)
    const tableWrapRef = useRef(null)
    const pageSize = usePricemanTablePageSize(tableWrapRef, { defaultRows: 30 })

    const [tableData, setTableData] = useState([])
    const [filterActive, setFilterActive] = useState(false)
    const [first, setFirst] = useState(0)
    const [timeSpanDialogOpen, setTimeSpanDialogOpen] = useState(false)
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false)
    const [bulkConfirmNext, setBulkConfirmNext] = useState(null)
    const [allSelected, setAllSelected] = useState(false)
    const [fromTime, setFromTime] = useState(new Date())
    const [toTime, setToTime] = useState(null)
    const [totalRecords, setTotalRecords] = useState(0)
    const [loading, setLoading] = useState(false)
    const [sortField, setSortField] = useState(null)
    const [sortOrder, setSortOrder] = useState('asc')
    const [filters, setFilters] = useState({
        reExclusion: { value: null, matchMode: FilterMatchMode.CONTAINS },
        reExclusionFrom: { value: null, matchMode: FilterMatchMode.CONTAINS },
        reExclusionTo: { value: null, matchMode: FilterMatchMode.CONTAINS },
        productnr: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
        productName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        productProducer: { value: null, matchMode: FilterMatchMode.CONTAINS },
        department: { value: null, matchMode: FilterMatchMode.CONTAINS },
        productCat: { value: null, matchMode: FilterMatchMode.CONTAINS },
        productGroup: { value: null, matchMode: FilterMatchMode.CONTAINS },
        price: { value: null, matchMode: FilterMatchMode.CONTAINS },
        stockLight: { value: null, matchMode: FilterMatchMode.CONTAINS }
    })

    const { t, i18n } = useTranslation()
    const lang = (i18n.language || "de").split("-")[0]
    const primeCalendarLocale = ["de", "fr", "en"].includes(lang) ? lang : "en"
    const primeCalendarDateFormat = lang === "de" ? "dd.mm.yy" : "dd/mm/yy"

    const onFilterChange = (e, field) => {
        const value = e.target.value
        const matchMode = field === "productnr" ? FilterMatchMode.STARTS_WITH : FilterMatchMode.CONTAINS
        const updatedFilters = { ...filters, [field]: { value, matchMode } }
        setFilters(updatedFilters)
    }

    const onFilter = (e) => {
        setFilters(e.filters)
    }

    const buildAllFilters = () => {
        const allFilters = {}
        
        if (filterActive) {
            allFilters.reExclusion = true
        }
        
        Object.entries(filters).forEach(([field, filterObj]) => {
            if (filterObj.value) {
                allFilters[field === "productnr" ? "productnr:STARTS_WITH" : field] = filterObj.value
            }
        })
        
        return allFilters
    }



    const getRowClass = (rowData) => {
        if (rowData.reExclusion) {
            const now = new Date()
            const from = Date.parse(rowData.reExclusionFrom)
            if (from && now < from) {
                return { "bg-warning": true }
            }
            const to = Date.parse(rowData.reExclusionTo)
            if (to && now > to) {
                return { "bg-success": true }
            }

            return { "bg-danger": true }
        }
    }

    const onPageChange = (event) => {
        setFirst(event.first)
        window.scrollTo(0, 0)
    }

    useEffect(() => {
        setFirst(0)
    }, [pageSize])

    const onSort = (event) => {
        if (event.sortField) {
            setSortField(event.sortField)
            setSortOrder(event.sortOrder === 1 ? 'asc' : 'desc')
        } else {
            setSortField(null)
            setSortOrder('asc')
        }
    }

    useEffect(() => {
        async function fetchTotalCount() {
            const allFilters = buildAllFilters()
            const count = await RuleEngineBackend.getExcludedArticlesCount(customerId, token, allFilters)
            if (count !== null) {
                setTotalRecords(count)
            }
        }
        fetchTotalCount()
        setFirst(0) 
    }, [customerId, token, filterActive, filters, buildAllFilters])

    useEffect(() => {
        async function fetchData() {
            setLoading(true)
            const pageNumber = Math.floor(first / pageSize)
            const allFilters = buildAllFilters()
            const excludedArticles = await RuleEngineBackend.getExcludedArticles(
                customerId,
                token,
                pageNumber,
                pageSize,
                allFilters,
                sortField,
                sortOrder
            )
            if (excludedArticles) {
                setTableData(fillTableData(excludedArticles))
            }
            setLoading(false)
        }
        fetchData()
    }, [customerId, token, filterActive, first, pageSize, filters, sortField, sortOrder])

    const renderColumnFilter = (field, header) => {
        return (
            <input
                type="text"
                value={filters[field]?.value || ""}
                onChange={(e) => onFilterChange(e, field)}
                placeholder={`${header}`}
                className="p-inputtext p-column-filter"
                style={{
                    width: "100%",
                    fontSize: "12px",
                    padding: "6px 8px",
                    border: "1px solid #ced4da",
                    borderRadius: "4px"
                }}
            />
        )
    }

    const toggleArticle = (articleId) => {
        setTableData((prevTableData) => {
            const newTableData = prevTableData.map((row) =>
                row.id === articleId ? { ...row, checked: !row.checked } : row
            )
            updateAllSelectedState(newTableData)
            return newTableData
        })
    }

    const toggleAllArticles = () => {
        const newAllSelectedState = !allSelected
        setAllSelected(newAllSelectedState)

        setTableData((prevTableData) =>
            prevTableData.map((row) => ({ ...row, checked: newAllSelectedState }))
        )
    }

    const updateAllSelectedState = (tableData) => {
        const totalRows = tableData.length
        const checkedRows = tableData.filter(row => row.checked).length
        setAllSelected(totalRows > 0 && checkedRows === totalRows)
    }

    const fillTableData = (tableData) => {
        const filledData = tableData.map((row, index) => {
            row.stockLightComp = (
                <p key={`row_stock_${row.id}_${index}`} className={`text-center`}>
                    {row.stockLight === "green" ? t("single.in_stock") : t("single.not_in_stock")}
                </p>
            )
            if (row.checked === undefined) {
                row.checked = false
            }
            return row
        })
        
        setTimeout(() => updateAllSelectedState(filledData), 0)
        
        return filledData
    }

    const isDeactivateButtonDiabled = () => {
        const rows = tableData.filter((row) => row.checked)
        if (rows.length <= 0) return true
        if (rows.some((entry) => entry.reExclusion)) return true
        return false
    }

    const isActivateButtonDiabled = () => {
        const rows = tableData.filter((row) => row.checked)
        if (rows.length <= 0) return true
        if (rows.some((entry) => !entry.reExclusion)) return true
        return false
    }

    const stripArticleForServer = (row) => {
        const copy = { ...row }
        delete copy.stockLightComp
        delete copy.checked
        return copy
    }

    const toggle = async (from, to) => {
        const fromStr = from ? PPUtils.formatDateForBackend(from) : null
        const toStr = to ? PPUtils.formatDateForBackend(to) : null
        const payload = {}
        const newTableData = tableData.map((row) => {
            if (!row.checked) {
                return row
            }
            const updated = { ...row }
            updated.checked = false
            if (updated.reExclusion) {
                updated.reExclusion = false
                updated.reExclusionTo = null
                updated.reExclusionFrom = null
            } else {
                updated.reExclusion = true
                updated.reExclusionFrom = fromStr
                updated.reExclusionTo = toStr
            }
            payload[updated.id] = stripArticleForServer(updated)
            return updated
        })
        if (Object.keys(payload).length === 0) {
            toast.error(t("pricemanagement_page.overview_page.toast_error"))
            return
        }
        const response = await RuleEngineBackend.updateArticlesBulk(token, payload)
        if (response && response.ok) {
            toast.success(t("single.updated"))
            setTableData(newTableData)
            setAllSelected(false)
            setFirst(0)
        } else {
            toast.error(t("pricemanagement_page.overview_page.toast_error"))
        }
    }

    const handleEnableClick = () => {
        const selectedRows = tableData.filter((row) => row.checked)
        if (selectedRows.length > SELECTED_ARTICLES_CONFIRM_THRESHOLD) {
            setBulkConfirmNext("timeSpan")
            setConfirmationDialogOpen(true)
        } else {
            setTimeSpanDialogOpen(true)
        }
    }

    const handleRemoveExclusionClick = async () => {
        const selectedRows = tableData.filter((row) => row.checked)
        if (selectedRows.length > SELECTED_ARTICLES_CONFIRM_THRESHOLD) {
            setBulkConfirmNext("remove")
            setConfirmationDialogOpen(true)
        } else {
            await toggle(fromTime, toTime)
        }
    }

    const proceedAfterBulkConfirm = async () => {
        const next = bulkConfirmNext
        setConfirmationDialogOpen(false)
        setBulkConfirmNext(null)
        if (next === "timeSpan") {
            setTimeSpanDialogOpen(true)
        } else if (next === "remove") {
            await toggle(fromTime, toTime)
        }
    }

    const closeBulkConfirmDialog = () => {
        setConfirmationDialogOpen(false)
        setBulkConfirmNext(null)
    }

    const columns = [
        {
            field: "reExclusionComp",
            header: t("pricemanagement_page.excluded_articles.table_header_reExclusionComp"),
            filterable: false,
            body: (row) => (
                <input
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleArticle(row.id)}
                    className="form-check-input m-auto"
                    type="checkbox"
                    checked={!!row.checked}
                    id={`excluded-check-${row.id}`}
                />
            )
        },
        {
            field: "reExclusionFrom",
            header: t("pricemanagement_page.excluded_articles.table_header_reExclusionFrom"),
            filterable: true,
            body: (row) => PPUtils.formatDateDE(row.reExclusionFrom)
        },
        {
            field: "reExclusionTo",
            header: t("pricemanagement_page.excluded_articles.table_header_reExclusionTo"),
            filterable: true,
            body: (row) => PPUtils.formatDateDE(row.reExclusionTo)
        },
        {
            field: "productnr",
            header: t("pricemanagement_page.excluded_articles.table_header_productnr"),
            filterable: true
        },
        {
            field: "productName",
            header: t("pricemanagement_page.excluded_articles.table_header_productName"),
            filterable: true
        },
        {
            field: "productProducer",
            header: t("pricemanagement_page.excluded_articles.table_header_productProducer"),
            filterable: true
        },
        {
            field: "department",
            header: t("pricemanagement_page.excluded_articles.table_header_department"),
            filterable: true
        },
        { field: "productCat", header: t("pricemanagement_page.excluded_articles.table_header_category"), filterable: true },
        { field: "productGroup", header: t("pricemanagement_page.excluded_articles.table_header_group"), filterable: true },
        { field: "price", header: t("pricemanagement_page.excluded_articles.table_header_price"), filterable: true },
        {
            field: "stockLightComp",
            header: t("pricemanagement_page.excluded_articles.table_header_stockLightComp"),
            filterable: true
        }
    ]

    if (customerId == null) {
        return (
            <div className="container-fluid priceman-page">
                <div className="card shadow">
                    <div className="card-body">
                        <PPMatchingStatusBanner variant="no_customer" />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>  
            <div className="container-fluid priceman-page">
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow">
                            <div className="card-body">
                                <div className="p-3 d-flex flex-column priceman-page-body">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex gap-2 flex-wrap align-items-center">
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={toggleAllArticles}>
                                                {allSelected
                                                    ? t("pricemanagement_page.excluded_articles.button_clear_row_selection")
                                                    : t("pricemanagement_page.excluded_articles.button_select_all_rows")}
                                            </button>
                                            <button
                                                className="btn btn-primary"
                                                onClick={handleEnableClick}
                                                disabled={isDeactivateButtonDiabled()}>
                                                {t("pricemanagement_page.excluded_articles.button_exclude_from_rules")}
                                            </button>
                                            <button
                                                className="btn btn-danger"
                                                onClick={handleRemoveExclusionClick}
                                                disabled={isActivateButtonDiabled()}>
                                                {t("pricemanagement_page.excluded_articles.button_remove_exclusion")}
                                            </button>
                                        </div>

                                        <div className="d-flex flex-row">
                                            <div className="col-md-2">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        checked={filterActive}
                                                        onChange={() => setFilterActive(!filterActive)}
                                                    />
                                                    <label className="form-check-label text-nowrap">
                                                        {t("pricemanagement_page.excluded_articles.excluded_articles_only")}
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="d-flex flex-row mt-2 priceman-table-row">
                                        <div className="col-12 priceman">
                                            <div className="priceman-table-wrap priceman-table-wrap--paged" ref={tableWrapRef}>
                                                <DataTable
                                            size="small"
                                            value={tableData}
                                            globalFilterFields={[
                                                "reExclusion",
                                                "reExclusionFrom",
                                                "reExclusionTo",
                                                "productnr",
                                                "productName",
                                                "productProducer",
                                                "department",
                                                "productCat",
                                                "productGroup",
                                                "price"
                                            ]}
                                            filters={filters}
                                            onFilter={onFilter}
                                            onSort={onSort}
                                            filterDisplay="row"
                                            sortMode="multiple"
                                            stripedRows
                                            lazy
                                            paginator
                                            first={first}
                                            rows={pageSize}
                                            totalRecords={totalRecords}
                                            onPage={onPageChange}
                                            loading={loading}
                                            onRowClick={(e) => toggleArticle(e.data.id)}
                                            scrollable
                                            scrollHeight="flex"
                                            rowClassName={getRowClass}
                                            removableSort>
                                            {columns.map((col, i) => (
                                                <Column
                                                    key={col.field + "_excludedArticles_" + i}
                                                    field={col.field}
                                                    header={col.header}
                                                    body={col.body}
                                                    headerStyle={{
                                                        border: "1px solid #ddd",
                                                        backgroundColor: "#f8f9fa",
                                                        fontWeight: "600",
                                                        fontSize: "14px",
                                                        padding: "12px 8px",
                                                        textAlign: "center",
                                                        color: "#495057",
                                                        verticalAlign: "middle"
                                                    }}
                                                    filterHeaderStyle={{
                                                        border: "1px solid #ddd",
                                                        backgroundColor: "#ffffff",
                                                        padding: "8px"
                                                    }}
                                                    sortable={true}
                                                    showFilterMenu={false}
                                                    filter={col.filterable}
                                                    filterMatchModeOptions={[{ label: 'contains', value: FilterMatchMode.CONTAINS }]}
                                                    filterElement={renderColumnFilter(col.field, col.header)}
                                                    style={{ minWidth: "auto" }}
                                                />
                                            ))}
                                        </DataTable>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            <Dialog
                header={t("pricemanagement_page.excluded_articles.dialog_title")}
                footer={
                    <>
                        <button
                            className="btn btn-primary btn-lg m-2"
                            onClick={() => {
                                toggle(fromTime, toTime)
                                setTimeSpanDialogOpen(false)
                            }}>
                            {t("single.ok")}
                        </button>
                        <button className="btn btn-danger btn-lg m-2" onClick={() => setTimeSpanDialogOpen(false)}>
                            {t("single.cancel")}
                        </button>
                    </>
                }
                visible={timeSpanDialogOpen}
                style={{ width: "43vw" }}
                onHide={() => {
                    setTimeSpanDialogOpen(false)
                    setFromTime(new Date())
                    setToTime(null)
                }}>
                <div className="d-flex flex-row">
                    <div className="d-flex col-md-auto align-items-center">
                        <p className="mb-0 me-2">
                            {t("pricemanagement_page.excluded_articles.table_header_reExclusionFrom") + ": "}
                        </p>
                        <Calendar
                            value={fromTime}
                            required
                            showTime={false}
                            onChange={(e) => setFromTime(e.value)}
                            locale={primeCalendarLocale}
                            dateFormat={primeCalendarDateFormat}
                        />
                    </div>
                    <div className="d-flex col-md-auto align-items-center ms-4">
                        <p className="mb-0 me-2">
                            {t("pricemanagement_page.excluded_articles.table_header_reExclusionTo") + ": "}
                        </p>
                        <Calendar
                            value={toTime}
                            showTime={false}
                            onChange={(e) => setToTime(e.value)}
                            locale={primeCalendarLocale}
                            dateFormat={primeCalendarDateFormat}
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog
                header={t("single.confirmation")}
                footer={
                    <>
                        <button className="btn btn-danger btn-lg m-2" onClick={closeBulkConfirmDialog}>
                            {t("single.cancel")}
                        </button>
                        <button
                            className="btn btn-primary btn-lg m-2"
                            onClick={proceedAfterBulkConfirm}>
                            {t("single.continue")}
                        </button>
                    </>
                }
                visible={confirmationDialogOpen}
                style={{ width: "40vw" }}
                onHide={closeBulkConfirmDialog}>
                <p className="mb-0">
                    {t("pricemanagement_page.excluded_articles.confirm_bulk_selection")}
                </p>
            </Dialog>
        </>
    )
}
